import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, Eye, ShieldCheck, ShieldOff } from "lucide-react";
import { Link } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { StatusDot } from "@/components/StatusDot";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api, type OpsHealth, type Overview } from "@/lib/api";
import { cn, fmtAgo } from "@/lib/utils";

export default function OverviewPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["overview"],
    queryFn: () => api.get<Overview>("/api/overview"),
    refetchInterval: 15_000,
  });
  const { data: health } = useQuery({
    queryKey: ["ops-health"],
    queryFn: () => api.get<OpsHealth>("/api/ops/health"),
    refetchInterval: 10_000,
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-8 w-40" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-56" />
      </div>
    );
  }

  const c = data.counters_24h;
  const total = c.allowed + c.blocked + c.challenged + c.errors;
  const blockRate = total > 0 ? ((c.blocked / total) * 100).toFixed(1) : "0.0";

  return (
    <div className="space-y-5">
      <PageHeader
        title="Overview"
        description={`${data.distinct_requests_24h.toLocaleString()} requests in the last 24h — ${blockRate}% blocked · last event ${fmtAgo(data.data_freshness.last_event_at)}`}
      />

      {health && health.status !== "ok" && (
        <Link to="/ops">
          <Card className={cn("border transition-colors",
            health.status === "down" ? "border-red-500/40 bg-red-500/5 hover:bg-red-500/10" : "border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10")}>
            <CardContent className="flex items-center gap-3 px-5 py-3.5">
              <AlertTriangle className={cn("h-4 w-4", health.status === "down" ? "text-red-300" : "text-amber-300")} />
              <div className="text-sm">
                <b className="capitalize">{health.status}</b> control plane —{" "}
                {health.reasons[0] ?? "see system status"}
              </div>
            </CardContent>
          </Card>
        </Link>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Allowed (24h)" value={c.allowed} icon={ShieldCheck} tone="text-emerald-300" />
        <StatCard label="Blocked (24h)" value={c.blocked} icon={ShieldOff} tone="text-red-300" />
        <StatCard label="Challenged" value={c.challenged} icon={Activity} tone="text-amber-300" />
        <StatCard label="Errors" value={c.errors} icon={AlertTriangle} tone="text-orange-300" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="card-hover">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Edge nodes</CardTitle>
            <CardDescription>{data.nodes.healthy}/{data.nodes.total} healthy</CardDescription>
          </CardHeader>
          <CardContent>
            {data.nodes.items.map((n) => (
              <div key={n.hostname} className="flex items-center justify-between border-b border-border/50 py-1.5 text-sm last:border-0">
                <span className="flex items-center gap-2 font-mono text-xs">
                  <StatusDot status={n.status} /> {n.hostname}
                </span>
                <span className="text-[11px] text-muted-foreground">{fmtAgo(n.last_seen_at)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Applications</CardTitle>
            <CardDescription>{data.apps.total} onboarded</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2"><StatusDot status="active" />Blocking</span>
              <b className="num">{data.apps.active}</b>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2"><StatusDot status="detect" />Detection-only</span>
              <b className="num">{data.apps.detect}</b>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2"><StatusDot status="draft" />Draft</span>
              <b className="num">{data.apps.draft}</b>
            </div>
            <div className="pt-2">
              <Link to="/applications" className="text-xs text-primary hover:underline">
                manage applications →
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Eye className="h-4 w-4" /> Controls fired (24h)
            </CardTitle>
            <CardDescription>which engine produced the verdicts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            {Object.entries(data.per_control_24h)
              .sort(([, a], [, b]) => b - a)
              .map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="capitalize text-muted-foreground">{k.replace(/_/g, " ")}</span>
                  <b className="num">{v}</b>
                </div>
              ))}
            {Object.keys(data.per_control_24h).length === 0 && (
              <p className="text-xs text-muted-foreground">No verdicts recorded yet.</p>
            )}
            {data.data_freshness.collection_gap_suspected && (
              <p className="pt-2 text-xs text-amber-300">
                No telemetry received yet — collector may be starting up.
              </p>
            )}
            {c.uninspected > 0 && (
              <p className="pt-2 text-xs text-muted-foreground">
                <Badge variant="secondary" className="mr-1">{c.uninspected}</Badge>
                requests passed uninspected — never counted as protected.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
